import { Selector, createSelector } from "@reduxjs/toolkit";
import {
  Issue,
  FieldMetadataForTypes as FieldMetadata,
  IssueStatus,
  IssueType,
  IssueAttributes,
} from "@adsk/acc-issues-types";
import { getIssueTypeEntitiesMap } from "@adsk/acc-issues-core/state/redux/ducks/issueTypes";
import { getIssueValueFromFieldMetadata } from "./issueStandardFields";
import { getWatcherProp } from "@adsk/acc-issues-core/state/redux/ducks/watchers";

export const getIsIssueValueEmpty = (value: any) => {
  return (
    value === null ||
    value === undefined ||
    value === "" ||
    (Array.isArray(value) && value.length === 0)
  );
};

export const getAreAllRequiredFieldsFilled = ({
  fieldsMetadata,
  issue,
  watcherProp,
}: {
  fieldsMetadata: FieldMetadata[];
  issue: IssueAttributes | Issue;
  watcherProp: "watchers" | "watcherObjects";
}) => {
  const requiredFields = fieldsMetadata.filter(({ required }) => !!required);
  return requiredFields.every(field => {
    const value = getIssueValueFromFieldMetadata({
      field,
      issue,
      watcherProp,
    });

    return !getIsIssueValueEmpty(value);
  });
};

/**
 * Returns the default issue status for a given issue based on required fields
 */
export const getDefaultIssueStatus: Selector<
  any,
  (issueData: Issue) => IssueStatus
> = createSelector(
  [getIssueTypeEntitiesMap, getWatcherProp],
  (issueTypeEntitiesMap, watcherProp) =>
    (issueData: Issue): IssueStatus => {
      const typeId = issueData?.issueTypeId;
      if (issueData.status !== IssueStatus.Draft && typeId) {
        const issueType: IssueType = issueTypeEntitiesMap[typeId] as IssueType;
        const areAllRequiredFieldsFilled = getAreAllRequiredFieldsFilled({
          fieldsMetadata: issueType?.fieldsMetadata ?? [],
          issue: issueData,
          watcherProp,
        });
        if (!areAllRequiredFieldsFilled) {
          return IssueStatus.Draft;
        }
      }
      return issueData.status || IssueStatus.Open;
    }
);
